import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TrainingProgressService } from '../../services/training-progress.service';
import { SafeHtmlPipe } from '../../shared/pipes/safe-html.pipe';
import { Icons } from '../../shared/icons';
import { Module, QuizQuestion } from '../../models/programme.model';
import { TranslatePipe } from '../../../filters/translate.pipe';

export interface QuizResult {
  moduleId: string;
  results: { question: QuizQuestion; userAnswer: number; isCorrect: boolean }[];
  score: number;
  correct: number;
  total: number;
  passed: boolean;
}

@Component({
  selector: 'app-quiz',
  standalone: true,
  imports: [CommonModule, SafeHtmlPipe, TranslatePipe],
  templateUrl: './quiz.component.html',
  styleUrls: ['../../training-styles.css'],
  encapsulation: ViewEncapsulation.None
})
export class QuizComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private trainingProgressService = inject(TrainingProgressService);

  readonly Icons = Icons;

  selectedProgramVersionCode = '';
  mod!: Module;
  moduleIndex = 0;
  passThreshold = 0.8;
  selectedAnswers: Record<string, number> = {};
  showError = false;
  startedAt: Date | undefined;

  ngOnInit(): void {
    const moduleId = this.route.snapshot.params['moduleId'];
    this.selectedProgramVersionCode = this.route.snapshot.queryParamMap.get('programVersionCode') || '';
    this.trainingProgressService.setProgramVersionCode(this.selectedProgramVersionCode);

    this.startedAt = new Date();
    Promise.all([
      this.trainingProgressService.loadModuleDetail(moduleId),
      this.trainingProgressService.loadSummary().catch(() => null)
    ]).then(([detail, summary]) => {
      if (!detail || !detail.module) {
        const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
        this.router.navigate(['/my-training', 'overview'], { queryParams });
        return;
      }
      this.mod = {
        ...detail.module,
        quiz: detail.questions || []
      } as any;
      this.moduleIndex = 0;
      this.passThreshold =
        summary?.passThreshold ??
        summary?.programme?.passThreshold ??
        summary?.program?.passThreshold ??
        detail?.programme?.passThreshold ??
        detail?.program?.passThreshold ??
        detail?.passThreshold ??
        this.passThreshold;
      this.selectedAnswers = {};
    });
  }

  selectAnswer(questionId: string, optionIndex: number): void {
    this.selectedAnswers[questionId] = optionIndex;
    this.showError = false;
  }

  isSelected(questionId: string, optionIndex: number): boolean {
    return this.selectedAnswers[questionId] === optionIndex;
  }

  submitQuiz(): void {
    if (!this.mod) return;

    // Validate all questions answered
    const unanswered = this.mod.quiz.filter(q => this.selectedAnswers[q.id] === undefined);
    if (unanswered.length > 0) {
      this.showError = true;
      const el = document.getElementById(`question-${unanswered[0].id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const answers = this.mod.quiz.map(q => ({
      quizQuestionId: q.id,
      selectedIndex: this.selectedAnswers[q.id]
    }));

    this.trainingProgressService.submitQuiz(this.mod.id, answers, this.startedAt, undefined).then((attempt) => {
      const quizResult: QuizResult = {
        moduleId: this.mod.id,
        results: this.mod.quiz.map(q => ({
          question: q,
          userAnswer: this.selectedAnswers[q.id],
          isCorrect: (attempt?.answers || []).find(a => a.quizQuestionId === q.id)?.correct === true
        })),
        score: attempt?.score || 0,
        correct: attempt?.correctCount || 0,
        total: attempt?.totalQuestions || this.mod.quiz.length,
        passed: attempt?.passed || false
      };
      const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
      this.router.navigate(['/my-training', 'results', this.mod.id], { queryParams });
    });
  }

  goBack(): void {
    const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
    this.router.navigate(['/my-training', 'module', this.mod.id], { queryParams });
  }
}
